import React from 'react';
import styled from 'styled-components';
import type { WorkspaceArtefact } from '../../../store/api/workspaceApi';

/* ---------- styled ---------- */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const ArtefactToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 1.25rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
`;

const ArtefactTitle = styled.span`
  flex: 1;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ToolbarBtn = styled.button`
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-secondary);
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-subtle);
  }
`;

const CanvasFrame = styled.iframe`
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  border: none;
  background: var(--bg-primary, #0f172a);
`;

const ProvenanceLink = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1.25rem;
  border-top: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-muted);
  font-family: inherit;
  font-size: 0.8125rem;
  cursor: pointer;
  border-left: none;
  border-right: none;
  border-bottom: none;
  transition: color 0.2s;

  &:hover {
    color: var(--accent);
  }
`;

const EmptyCanvas = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--text-muted);
  font-size: 0.9375rem;
`;

/* ---------- component ---------- */

interface BuildCanvasProps {
  artefact: WorkspaceArtefact | null;
  html: string | null;
  url: string | null;
  onShare?: () => void;
  onDownload?: () => void;
}

const BuildCanvas: React.FC<BuildCanvasProps> = ({
  artefact,
  html,
  url,
  onShare,
  onDownload,
}) => {
  if (!artefact && !html && !url) {
    return (
      <EmptyCanvas>
        <div style={{ fontSize: '2rem' }}>&#x1F528;</div>
        <div>Built artefacts will appear here</div>
      </EmptyCanvas>
    );
  }

  return (
    <Wrapper>
      {artefact && (
        <ArtefactToolbar>
          <ArtefactTitle>{artefact.title}</ArtefactTitle>
          {onShare && <ToolbarBtn onClick={onShare}>Share</ToolbarBtn>}
          {onDownload && <ToolbarBtn onClick={onDownload}>Download</ToolbarBtn>}
        </ArtefactToolbar>
      )}

      {url ? (
        <CanvasFrame src={url} sandbox="allow-scripts" title="Build canvas" />
      ) : html ? (
        <CanvasFrame srcDoc={html} sandbox="allow-scripts" title="Build canvas" />
      ) : null}

      <ProvenanceLink>How was this built? &rarr;</ProvenanceLink>
    </Wrapper>
  );
};

export default BuildCanvas;
